// src/components/frontpage/LocalityFrontPage.jsx
// Wrapper for the Locality desktop front page (>=1200px).
// Five full-height columns, single row: Directory (fixed rail) · News · Events · Chat ·
// Groups (fixed-equal rail). Mobile and Groups context continue to use their own front pages
// (MobileFrontPage.jsx, GroupFrontPage.jsx — untouched by this geometry).
// Chat and event data come from App.js (real Supabase data).
// Marketplace (mock/session-local swap state) was retired from this page in favour of Groups
// (July 2026). SwapColumn/SwapCreateModal/SwapDetailModal and mockFrontPage.getSwapListings
// remain in the tree, unused here, for when Marketplace returns.
//
// Columns are placed directly (not via MultiViewGrid) — MultiViewGrid.jsx is left in the tree
// unused, a cleanup candidate, not touched here.

import React, { useState, useEffect } from 'react';
import DirectoryColumn from './columns/DirectoryColumn';
import ChatColumn from './columns/ChatColumn';
import EventsColumn from './columns/EventsColumn';
import GroupsColumn from './columns/GroupsColumn';
import ModeColumn from './columns/ModeColumn';
import MoreButton from './columns/MoreButton';
import NewsMiniGrid from './NewsMiniGrid';
import { useTranslation } from '../../i18n/useTranslation';
import { useTheme } from '../../themes/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseFetch } from '../../lib/supabase';
import { parseEventDate } from '../../utils/eventDateTime';
import useMediaQuery, { NEWS_DOUBLE_COLUMN_BREAKPOINT } from '../../hooks/useMediaQuery';

// Fixed rail width for Directory (left) — revisable pending Michael's visual check on localhost.
const DIRECTORY_WIDTH = '300px';

const LocalityFrontPage = ({
  articles = [],
  newsHasMore = false,
  // Load More for Events (Roadmap #104); the News column keeps its More, which opens the full view
  feedPaging = null,
  chats = [],
  events = [],
  onArticleClick,
  onChatClick,
  onEventClick,
  onModeSwitch,
  // Chat accordion props
  openChatThreadId = null,
  onChatThreadToggle,
  selectedArea,
  onChatsRefresh,
  // Directory column props
  currentUser = null,
  guestFallbackBoundaryId = null,
  visitBoundaryId = null,
  userHomeLocationName,
  userHomeLocationCoords,
  // Groups column props
  sessionReady = false,
  onGroupSelect
}) => {
  const { t } = useTranslation('frontpage');
  const { t: tArticles } = useTranslation('articles');
  const { windowWidth } = useMediaQuery();
  const theme = useTheme();
  const { requireAuth } = useAuth();

  // News "+" — same contract as HeaderButtonsFeed's triggerCreateAction 'news' case (desktop,
  // locality context: no groupId/groupName, no Walking Composer branch — that's mobile-only).
  const handleNewArticle = () => {
    requireAuth('gate_body_post', () => {
      window.dispatchEvent(new CustomEvent('ournet:navigate', {
        detail: { view: 'new-article', mode: 'add' }
      }));
    });
  };

  // Article creation is not available while visiting (Change 3, addendum-1 §A/§C) — hidden,
  // not disabled-with-message, matching the rest of the guard sites in this build.
  const isVisiting = !!visitBoundaryId;

  // Event details (start_date, venue_name etc.) from event_details table
  const [eventDetails, setEventDetails] = useState({});

  // Fetch event_details for real events (start_date, venue_name etc.)
  useEffect(() => {
    if (!events || events.length === 0) {
      setEventDetails({});
      return;
    }

    let cancelled = false;
    const fetchEventDetails = async () => {
      const eventIds = events.map(e => e.id).filter(Boolean);
      if (eventIds.length === 0) return;

      const { data, error } = await supabaseFetch.query('event_details', {
        select: 'article_id,start_date,end_date,display_from,venue_name,venue_address',
        inFilter: { article_id: eventIds }
      });

      if (error) {
        console.error('Error fetching event_details:', error);
        return;
      }

      if (!cancelled && data) {
        const detailsMap = {};
        for (const d of data) {
          detailsMap[d.article_id] = d;
        }
        setEventDetails(detailsMap);
      }
    };

    fetchEventDetails();
    return () => { cancelled = true; };
  }, [events]);

  // --- Chat data: filter to originals, sorted by most recent activity ---
  // Exclude orphan gravestones: soft-deleted roots with zero live replies
  const chatData = (chats || [])
    .filter(c => !c.parent_chat_id && !(c.is_deleted_by_author && (c.reply_count || 0) === 0))
    .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));

  // --- Event data: merge event_details, filter to the display window, sort soonest first ---
  const eventData = (events || [])
    .filter(e => e.article_type === 'event')
    .map(e => {
      const details = eventDetails[e.id];
      return {
        ...e,
        start_date: details?.start_date || null,
        end_date: details?.end_date || null,
        display_from: details?.display_from || null,
        venue_name: details?.venue_name || null,
        venue_address: details?.venue_address || null
      };
    })
    // Full roster — the Events column owns the Future/Past split (no feed-window pre-filter here).
    .sort((a, b) => parseEventDate(a.start_date) - parseEventDate(b.start_date));

  // Real news articles for the NewsBar (admit 'website' as news-family for display; locality
  // feeds never carry website embeds — those are group-scoped by community_id)
  const newsArticles = (articles || []).filter(a => a.article_type === 'news' || a.article_type === 'website');

  // News is 2fr (double-weight) only once its column is wide enough for NewsMiniGrid's 240px
  // cards to wrap 2-per-row; below that it matches Events/Chat/Groups at 1fr so the extra width
  // isn't wasted on a column still showing one card-column. Breakpoint derived from live grid
  // geometry — see NEWS_DOUBLE_COLUMN_BREAKPOINT's own comment in useMediaQuery.js.
  const newsColumnWeight = windowWidth >= NEWS_DOUBLE_COLUMN_BREAKPOINT ? '2fr' : '1fr';

  return (
    // Single-row, 5-column grid. Directory is fixed-width (DIRECTORY_WIDTH); News ratio is
    // viewport-responsive (see newsColumnWeight above), Events/Chat/Groups are equal 1fr, all
    // full height. columnGap 12px; no calc() height: the grid fills its flex cell at height:100%.
    <div style={{
      display: 'grid',
      gridTemplateColumns: `${DIRECTORY_WIDTH} minmax(0, ${newsColumnWeight}) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)`,
      columnGap: '12px',
      height: '100%',
      width: '100%',
      minHeight: 0,
      overflow: 'hidden',
      padding: '2px 12px 0 12px',
      boxSizing: 'border-box'
    }}>
      {/* Directory — full-height left rail, fixed width */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <DirectoryColumn
          selectedArea={selectedArea}
          currentUser={currentUser}
          guestFallbackBoundaryId={guestFallbackBoundaryId}
          visitBoundaryId={visitBoundaryId}
          userHomeLocationName={userHomeLocationName}
          userHomeLocationCoords={userHomeLocationCoords}
          hideColumnHeader={true}
          expandedLocalityStrip={true}
        />
      </div>

      {/* News — its own full-height column, header matches Events/Chat/Groups (title + "+" to
          write a new article) */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <ModeColumn
          title={t('news')}
          themeColor={theme.primary}
          onAdd={isVisiting ? undefined : handleNewArticle}
          addLabel={tArticles('new_article')}
          scrollBody={false}
          footer={
            <MoreButton
              hasMore={newsHasMore}
              onClick={() => onModeSwitch?.('news')}
              label={t('news_more')}
              color={theme.primary}
            />
          }
        >
          <div style={{ flex: 1, minHeight: 0 }}>
            <NewsMiniGrid articles={newsArticles} onArticleClick={onArticleClick} />
          </div>
        </ModeColumn>
      </div>

      {/* Events — its own full-height column */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <EventsColumn data={eventData} onClick={onEventClick} visitBoundaryId={visitBoundaryId} paging={feedPaging} />
      </div>

      {/* Chat — its own full-height column */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <ChatColumn
          data={chatData}
          openThreadId={openChatThreadId}
          onThreadToggle={onChatThreadToggle}
          selectedArea={selectedArea}
          onChatsRefresh={onChatsRefresh}
          visitBoundaryId={visitBoundaryId}
        />
      </div>

      {/* Groups — full-height right rail */}
      <div style={{ minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <GroupsColumn
          currentUser={currentUser}
          sessionReady={sessionReady}
          onGroupSelect={onGroupSelect}
          selectedArea={selectedArea}
        />
      </div>
    </div>
  );
};

export default LocalityFrontPage;
